import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import type { Tables } from '@/types/database.types';

type StatusUpdateRow = Tables<'status_updates'>;

export function StatusUpdateItem({
  item,
  type,
  onUpdate,
  onDelete,
  readOnly = false
}: {
  item: StatusUpdateRow;
  type: 'accomplishment' | 'priority';
  onUpdate: (content: string) => void;
  onDelete: () => void;
  readOnly?: boolean;
}) {
  const [content, setContent] = useState(item.content);
  const debouncedContent = useDebounce(content, 800);
  const initialRef = useRef(true);

  useEffect(() => {
    if (!initialRef.current && debouncedContent !== item.content) {
      onUpdate(debouncedContent);
    }
    initialRef.current = false;
  }, [debouncedContent, item.content, onUpdate]);

  return (
    <li className="flex items-center gap-2 group">
      <span className={cn(
        "flex-shrink-0",
        type === 'accomplishment' ? "text-green-500" : "text-blue-500"
      )}>
        {type === 'accomplishment' ? '✓' : '→'}
      </span>
      <Input
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="flex-1 border-0 bg-transparent px-0 h-auto focus-visible:ring-0 text-sm"
        placeholder={type === 'accomplishment' ? 'What was accomplished?' : 'What is the priority?'}
        readOnly={readOnly}
      />
      {!readOnly && (
        <button
          onClick={onDelete}
          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}
